import { useCallback, useEffect, useState } from 'react';
import { Friend, FriendListResp } from '@/network/RestApi';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { useNotificationStore } from '@/state/notificationStore';
import { ui } from './theme';
import { CloseButton } from './CloseButton';

export function FriendPanel() {
  const ch = useCharacterStore((s) => s.current);
  const close = () => useUIStore.getState().set('friend', false);
  const showToast = useUIStore((s) => s.showToast);
  const refreshTick = useNotificationStore((s) => s.refreshTickFriend);
  const setFriendRequests = useNotificationStore((s) => s.setFriendRequests);
  const [data, setData] = useState<FriendListResp | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!ch) return;
    try {
      const r = await Friend.list(ch.id);
      setData(r);
      setFriendRequests(r.requests.incoming.length);
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  }, [ch, showToast, setFriendRequests]);

  useEffect(() => { reload(); }, [reload, refreshTick]);
  useEffect(() => {
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
  }, [reload]);

  if (!ch) return null;

  const send = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const r = await Friend.request(ch.id, { targetName: name.trim() });
      showToast(r.accepted ? 'Đã kết bạn ngay' : 'Đã gửi lời mời');
      setName('');
      reload();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      showToast(ok);
      reload();
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  };

  return (
    <div
      data-block-game-input="true"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'absolute', top: 70, right: 340, width: 340, ...ui.panel }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong>Bạn bè</strong>
        <CloseButton onClick={close} />
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <input
          style={{ ...ui.input, flex: 1 }}
          placeholder="Tên nhân vật..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
        />
        <button style={ui.primaryBtn} disabled={busy} onClick={send}>Mời</button>
      </div>

      {!data && <div style={{ fontSize: 12, opacity: 0.7 }}>Đang tải...</div>}

      {data && (
        <>
          {data.requests.incoming.length > 0 && (
            <Section title={`Lời mời đến (${data.requests.incoming.length})`}>
              {data.requests.incoming.map((r) => (
                <Row key={r.id} text={r.otherName}>
                  <button style={miniBtn('#4caf50')} onClick={() => act(() => Friend.accept(ch.id, { requestId: r.id }), 'Đã chấp nhận')}>OK</button>
                  <button style={miniBtn('#9e9e9e')} onClick={() => act(() => Friend.reject(ch.id, { requestId: r.id }), 'Đã từ chối')}>X</button>
                </Row>
              ))}
            </Section>
          )}

          {data.requests.outgoing.length > 0 && (
            <Section title={`Đã gửi (${data.requests.outgoing.length})`}>
              {data.requests.outgoing.map((r) => (
                <Row key={r.id} text={r.otherName}>
                  <button style={miniBtn('#9e9e9e')} onClick={() => act(() => Friend.cancel(ch.id, { requestId: r.id }), 'Đã hủy')}>Hủy</button>
                </Row>
              ))}
            </Section>
          )}

          <Section title={`Bạn bè (${data.friends.length})`}>
            {data.friends.length === 0
              ? <div style={{ fontSize: 12, opacity: 0.6 }}>Chưa có bạn bè</div>
              : data.friends.map((f) => (
                <Row key={f.characterId} text={`${f.name}  · Lv ${f.level} ${f.class}`}>
                  <button style={miniBtn('#b04a4a')} onClick={() => act(() => Friend.remove(ch.id, f.characterId), 'Đã xóa')}>X</button>
                </Row>
              ))}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </div>
  );
}

function Row({ text, children }: { text: string; children?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.25)', padding: '4px 8px', borderRadius: 4 }}>
      <span style={{ fontSize: 13 }}>{text}</span>
      <div style={{ display: 'flex', gap: 4 }}>{children}</div>
    </div>
  );
}

function miniBtn(color: string): React.CSSProperties {
  return {
    background: color,
    color: '#fff',
    border: 'none',
    borderRadius: 3,
    padding: '2px 8px',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 700,
  };
}
